import { useEffect, useState } from 'react';
import { callLogApi } from '../../services/api/call-log.api';
import { CallActivityLogEntry } from '../../services/api/call-log.types';
import { sortEventsChronologically } from './call-activity-event-details.utils';

interface UseCallTraceEventsOptions {
  callTraceId?: string | null;
  cxoneContactId?: string | null;
  enabled?: boolean;
}

interface UseCallTraceEventsResult {
  events: CallActivityLogEntry[];
  isLoading: boolean;
  error: string | null;
}

export function useCallTraceEvents({
  callTraceId,
  cxoneContactId,
  enabled = true,
}: UseCallTraceEventsOptions): UseCallTraceEventsResult {
  const [events, setEvents] = useState<CallActivityLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || (!callTraceId && !cxoneContactId)) {
      setEvents([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    async function loadEvents() {
      setIsLoading(true);
      setError(null);

      try {
        const response = await callLogApi.list({
          callTraceId: callTraceId ?? undefined,
          cxoneContactId: !callTraceId ? (cxoneContactId ?? undefined) : undefined,
          limit: 100,
        });
        if (!cancelled) {
          setEvents(sortEventsChronologically(response.data));
        }
      } catch (err) {
        if (!cancelled) {
          setEvents([]);
          setError(err instanceof Error ? err.message : 'Failed to load call events');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadEvents();

    return () => {
      cancelled = true;
    };
  }, [enabled, callTraceId, cxoneContactId]);

  return { events, isLoading, error };
}
